/**
 * useImmersiveCatalogNav
 * Builds a single flat, ordered list of feed entries for the immersive overlay
 * to swipe through, plus a department index used by the "jump to department"
 * control.
 *
 * The feed MIRRORS the normal catalog exactly:
 *   - Items come from the same source (allDisplayableItems) and pass the same
 *     applyItemFilters (out-of-stock / kosher / rabanut / not-in-assortment),
 *     so the immersive feed shows precisely what the catalog grid would show.
 *   - Only departments the catalog sidebar shows (visibleDepartments) appear;
 *     items whose department is hidden or unknown are excluded.
 *   - The virtual "מבצעים" department is prepended when promotions exist, in
 *     catalog order: first-order multi-item promo CARDS, first-order single
 *     promo items, regular multi-item promo cards, regular single promo items.
 *
 * Multi-item promotions become synthetic "promo card" entries:
 *   { __promoCard: true, promo, makat: '__promo__<id>', name, image,
 *     department: '__promotions__' }
 * The overlay renders these with ImmersivePromoPane instead of ImmersivePane.
 * Single-promo items are shallow copies re-homed to the promo department so
 * the top bar / jump / exit-sync treat them as promo-department entries (the
 * originals still appear later under their real departments, like the grid).
 *
 * Ordering inside real departments mirrors the catalog: department `order` ->
 * sub-department `order` -> not-in-assortment last -> item `order` ->
 * alphabetical fallback by Hebrew name. Requires `catalogStore.loadAllSubs()`
 * for correct sub-department ordering (the immersive view triggers it).
 */
import { computed } from 'vue'
import { useCatalogStore } from '../../app/store/catalog.js'
import { getPromoMakats } from '../utils/promoCalc.js'

const ORDER_FALLBACK = 99999

export function useImmersiveCatalogNav() {
  const catalogStore = useCatalogStore()

  // Department order/name lookups — built from the SAME list the catalog
  // sidebar renders (visibleDepartments), so hidden departments never leak
  // into the feed. Includes the virtual promo department when promos exist.
  const visibleDeptById = computed(() => {
    const map = new Map()
    for (const dept of catalogStore.visibleDepartments) map.set(dept.id, dept)
    return map
  })

  // Sub-department order lookup, keyed by `${deptId}::${subId}` (sub ids can
  // repeat across departments, so they must be namespaced by department).
  const subOrderByKey = computed(() => {
    const map = new Map()
    for (const [deptId, subs] of catalogStore.subsByDepartmentId) {
      subs.forEach((sub, index) => {
        const order = typeof sub.order === 'number' ? sub.order : index
        map.set(`${deptId}::${sub.id}`, order)
      })
    }
    return map
  })

  // ── Promo department entries (prepended) ────────────────────────
  function promoCardEntry(promo) {
    // Card image: the promotion's own image, else the first available item's.
    let image = promo.image || null
    if (!image) {
      for (const makat of getPromoMakats(promo)) {
        const item = catalogStore.itemsByMakat.get(makat)
        if (item?.image) {
          image = item.image
          break
        }
      }
    }
    return {
      __promoCard: true,
      promo,
      makat: `__promo__${promo.id}`,
      name: promo.label || 'מבצע',
      image,
      department: catalogStore.PROMO_DEPARTMENT_ID,
      subDepartment: null,
    }
  }

  function promoItemEntry(item) {
    // Shallow copy re-homed to the promo department (top bar + exit-sync).
    return { ...item, department: catalogStore.PROMO_DEPARTMENT_ID, subDepartment: null }
  }

  const promoFeedEntries = computed(() => {
    if (!visibleDeptById.value.has(catalogStore.PROMO_DEPARTMENT_ID)) return []
    const entries = []
    for (const promo of catalogStore.firstOrderMultiPromos) entries.push(promoCardEntry(promo))
    for (const item of catalogStore.applyItemFilters(catalogStore.firstOrderSinglePromoItems)) {
      entries.push(promoItemEntry(item))
    }
    for (const promo of catalogStore.regularMultiPromos) entries.push(promoCardEntry(promo))
    for (const item of catalogStore.applyItemFilters(catalogStore.regularSinglePromoItems)) {
      entries.push(promoItemEntry(item))
    }
    return entries
  })

  // ── Flat ordered feed: promo department first, then real departments ──
  const orderedItems = computed(() => {
    const deptById = visibleDeptById.value
    const sOrder = subOrderByKey.value
    const promoId = catalogStore.PROMO_DEPARTMENT_ID

    // Same items + same filters as the catalog grid; only reachable (visible,
    // real) departments make it into the feed.
    const items = catalogStore
      .applyItemFilters(catalogStore.allDisplayableItems)
      .filter((it) => it.department !== promoId && deptById.has(it.department))

    const sorted = [...items].sort((a, b) => {
      const da = deptById.get(a.department)
      const db = deptById.get(b.department)
      const doA = typeof da?.order === 'number' ? da.order : ORDER_FALLBACK
      const doB = typeof db?.order === 'number' ? db.order : ORDER_FALLBACK
      if (doA !== doB) return doA - doB

      const sa = sOrder.has(`${a.department}::${a.subDepartment}`)
        ? sOrder.get(`${a.department}::${a.subDepartment}`)
        : ORDER_FALLBACK
      const sb = sOrder.has(`${b.department}::${b.subDepartment}`)
        ? sOrder.get(`${b.department}::${b.subDepartment}`)
        : ORDER_FALLBACK
      if (sa !== sb) return sa - sb

      // Not-in-assortment items sort last within their sub (mirrors the grid).
      const niaA = a._notInAssortment ? 1 : 0
      const niaB = b._notInAssortment ? 1 : 0
      if (niaA !== niaB) return niaA - niaB

      const oa = typeof a.order === 'number' ? a.order : ORDER_FALLBACK
      const ob = typeof b.order === 'number' ? b.order : ORDER_FALLBACK
      if (oa !== ob) return oa - ob

      return (a.name || '').localeCompare(b.name || '', 'he')
    })

    return promoFeedEntries.value.length ? [...promoFeedEntries.value, ...sorted] : sorted
  })

  // Departments present in the feed, in feed order, with first-item index + count
  const departmentsForJump = computed(() => {
    const deptById = visibleDeptById.value
    const result = []
    const byId = new Map()
    orderedItems.value.forEach((it, index) => {
      const id = it.department
      if (!byId.has(id)) {
        const entry = { id, name: deptById.get(id)?.name || id, firstIndex: index, count: 0 }
        byId.set(id, entry)
        result.push(entry)
      }
      byId.get(id).count++
    })
    return result
  })

  function firstIndexOfDepartment(deptId) {
    const entry = departmentsForJump.value.find((d) => d.id === deptId)
    return entry ? entry.firstIndex : -1
  }

  // First feed index for a department + (optional) sub-department. Prefers an
  // exact dept+sub match; falls back to the department's first item; -1 if none.
  function firstIndexOfDeptSub(deptId, subId) {
    if (!deptId) return -1
    const items = orderedItems.value
    if (subId) {
      const exact = items.findIndex((it) => it.department === deptId && it.subDepartment === subId)
      if (exact >= 0) return exact
    }
    return items.findIndex((it) => it.department === deptId)
  }

  // Prev/next sub-department jump targets from a feed index, bounded WITHIN
  // the current department (crossing into another department hides the jump).
  // Both targets are the FIRST item of their sub's contiguous run; -1 = none.
  // Items without a sub (and promo-department entries) form their own "null"
  // group, so a dept with a single sub (or no subs) yields -1/-1.
  function subJumpTargets(index) {
    const items = orderedItems.value
    const cur = items[index]
    if (!cur) return { prevIndex: -1, nextIndex: -1 }
    const dept = cur.department
    const sub = cur.subDepartment ?? null

    let nextIndex = -1
    for (let i = index + 1; i < items.length; i++) {
      const it = items[i]
      if (it.department !== dept) break
      if ((it.subDepartment ?? null) !== sub) {
        nextIndex = i
        break
      }
    }

    let prevIndex = -1
    for (let i = index - 1; i >= 0; i--) {
      const it = items[i]
      if (it.department !== dept) break
      if ((it.subDepartment ?? null) !== sub) {
        // Found the previous sub's LAST item — walk back to its first.
        const prevSub = it.subDepartment ?? null
        let j = i
        while (
          j - 1 >= 0 &&
          items[j - 1].department === dept &&
          (items[j - 1].subDepartment ?? null) === prevSub
        ) {
          j--
        }
        prevIndex = j
        break
      }
    }

    return { prevIndex, nextIndex }
  }

  // Sub-departments of a department, in feed order, with first-item index +
  // count — feeds the overlay's sub-category selector sheet (mirrors
  // departmentsForJump). Items without a sub form a trailing "כללי" group.
  function subsOfDepartment(deptId) {
    if (!deptId) return []
    const subsMeta = catalogStore.subsByDepartmentId.get(deptId) || []
    const nameById = new Map(subsMeta.map((s) => [s.id, s.name]))
    const result = []
    const byId = new Map()
    orderedItems.value.forEach((it, index) => {
      if (it.department !== deptId) return
      const id = it.subDepartment ?? null
      let entry = byId.get(id)
      if (!entry) {
        entry = {
          id,
          name: id != null ? nameById.get(id) || String(id) : 'כללי',
          firstIndex: index,
          count: 0,
        }
        byId.set(id, entry)
        result.push(entry)
      }
      entry.count++
    })
    return result
  }

  return {
    orderedItems,
    departmentsForJump,
    firstIndexOfDepartment,
    firstIndexOfDeptSub,
    subJumpTargets,
    subsOfDepartment,
  }
}
